/**
 * Sends a search straight to a landing page or collection when the query matches a Search Terms metaobject entry.
 * The term map comes from sections/search-terms-map.liquid.
 */

const SEARCH_TERMS_MAP_SECTION = 'search-terms-map';
const SEARCH_TERMS_MAP_STORAGE_KEY = 'search-terms-map';
const SEARCH_TERMS_MAP_WAIT_MS = 1500;

let searchTermsMapPromise = null;

function normaliseSearchTerm(term) {
  return term.toLowerCase().replace(/["'’]/g, '').trim();
}

function isInterceptableSearchForm(form) {
  if (!(form instanceof HTMLFormElement)) return false;

  const action = form.getAttribute('action') || form.action;
  if (!action) return false;

  try {
    return new URL(action, window.location.origin).pathname === '/search';
  } catch (error) {
    return action.trim() === '/search';
  }
}

function loadSearchTermsMap() {
  if (searchTermsMapPromise) return searchTermsMapPromise;

  try {
    const cached = sessionStorage.getItem(SEARCH_TERMS_MAP_STORAGE_KEY);
    if (cached) {
      searchTermsMapPromise = Promise.resolve(JSON.parse(cached));
      return searchTermsMapPromise;
    }
  } catch (error) {
    // Storage unavailable, fall through to fetching
  }

  searchTermsMapPromise = fetch(`${window.Shopify?.routes?.root || '/'}?section_id=${SEARCH_TERMS_MAP_SECTION}`)
    .then((response) => (response.ok ? response.text() : ''))
    .then((html) => {
      const script = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector('[data-search-terms-map]');
      const map = script ? JSON.parse(script.textContent) : {};

      try {
        sessionStorage.setItem(SEARCH_TERMS_MAP_STORAGE_KEY, JSON.stringify(map));
      } catch (error) {
        // Storage unavailable, the map is still kept in memory for this page
      }

      return map;
    })
    .catch(() => {
      searchTermsMapPromise = null;
      return {};
    });

  return searchTermsMapPromise;
}

function waitForSearchTermsMap() {
  return Promise.race([
    loadSearchTermsMap(),
    new Promise((resolve) => setTimeout(() => resolve({}), SEARCH_TERMS_MAP_WAIT_MS)),
  ]);
}

document.addEventListener('focusin', (event) => {
  const input = event.target;
  if (input instanceof HTMLInputElement && input.name === 'q' && isInterceptableSearchForm(input.form)) {
    loadSearchTermsMap();
  }
});

// Runs after the predictive search and empty-query handlers, so it skips submits they cancel
document.addEventListener('submit', (event) => {
  const form = event.target;
  if (event.defaultPrevented || !isInterceptableSearchForm(form)) return;

  const query = normaliseSearchTerm(new FormData(form).get('q')?.toString() || '');
  if (!query) return;

  event.preventDefault();

  waitForSearchTermsMap().then((map) => {
    if (map[query]) {
      window.location.assign(map[query]);
    } else {
      const params = new URLSearchParams(new FormData(form));
      // Strict "quoted" search, snippets/search-redirect.liquid falls back to unquoted when it finds nothing
      if (window.quotedSearchMode === 'phrase' || window.quotedSearchMode === 'words') {
        const term = params.get('q').replace(/"/g, '').trim().replace(/\s+/g, ' ');
        // "adidas" "socks" matches every word in any order, "adidas socks" the exact phrase
        params.set('q', window.quotedSearchMode === 'words' ? term.split(' ').map((word) => `"${word}"`).join(' ') : `"${term}"`);
        params.delete('fallback');
      }
      // Default to in stock, as on collections (assets/in-stock-default.js)
      if (!params.has('filter.v.availability')) params.set('filter.v.availability', '1');
      const url = new URL(form.getAttribute('action') || form.action, window.location.origin);
      window.location.assign(`${url.pathname}?${params}`);
    }
  });
});
